"use client";

import { type FC, useEffect, useState } from "react";
import { nextFrame, Portal, POI } from "@huub-web/ui";

import { Hotspot } from "../../Hotspot";
import type { HotspotRecord } from "../utils/mapMarkers";
import { Marker } from "../../Marker";
import { HotspotCluster } from "./HotspotCluster";

/**
 * HotspotController component props
 */
export type HotspotControllerProps = HotspotRecord & {
  /**
   * Callback function to execute when hotspot is added to the map
   **/
  add?: () => () => void;
};

/**
 * HotspotController component for displaying custom hotspot markers
 */
export const HotspotController: FC<HotspotControllerProps> = ({
  id,
  type = "marker",
  title,
  subtitle,
  indicatorVariant,
  icon,
  delay,
  tooltipHideDelay,
  tooltip,
  add,
  onClick,
  closable,
  active,
  onClose,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const remove = add?.();
    nextFrame(() => {
      setShow(true);
    });

    return () => {
      remove?.();
      setShow(false);
    };
  }, [add]);

  return (
    show && (
      <Portal id={id}>
        {type === "dot" && (
          <Hotspot
            title={title}
            subtitle={subtitle}
            indicatorVariant={indicatorVariant}
            tooltipHideDelay={tooltipHideDelay}
            delay={delay}
            tooltip={tooltip}
            onClick={onClick}
            icon={icon}
            closable={closable}
            active={active}
          />
        )}
        {type === "marker" && (
          <Marker
            title={title}
            subtitle={subtitle}
            indicatorVariant={indicatorVariant}
            delay={delay}
            tooltip={tooltip}
            onClick={onClick}
          />
        )}
        {type === "poi" && (
          <POI
            delay={delay}
            tooltip={tooltip}
            onClick={onClick}
            icon={icon}
            tooltipHideDelay={tooltipHideDelay}
            active={active}
            title={title}
            subtitle={subtitle}
            closable={closable}
            onClose={onClose}
          />
        )}
        {type === "cluster" && (
          <HotspotCluster id={id} title={title} onClick={onClick} />
        )}
      </Portal>
    )
  );
};
